import { useState } from 'react';
import { ExportDataDelimited } from '@fundacao/shared-components/esm/ie/renovacao/index';
import { UseCampanhaContext } from 'context/campanha-context';
import { CampanhaValues, CsvRegisterProp } from 'models/campanhas';
import {
  CampaignUploadProgressEvent,
  CampaignUploadResult,
  UseCreateCampaignReturn,
} from 'models/campaign-mkt';
import generatePdf from 'reports/log-import';
import campanhaService from 'services/campanha-service/campanha-service';
import {
  getCampaignModalInformation,
  getCampaignFileNameBase,
  getCampaignUploadFileName,
  getCreateCampaignPayload,
  isCreateCampaignFormInvalid,
  parseCampaignErrorCsv,
} from 'utils/campanha-utils';

export const useCreateCampaign = (
  audience: string,
): UseCreateCampaignReturn => {
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState(false);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [modalInformation, setModalInformation] = useState<CsvRegisterProp[]>(
    [],
  );
  const [campaignId, setCampaignId] = useState<number>();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { formCampanhaValues } = UseCampanhaContext();

  const campaignName = String(
    formCampanhaValues[CampanhaValues.NOME_CAMPANHA] || 'campanha',
  );
  const isFormInvalid = isCreateCampaignFormInvalid({
    audience,
    file,
    fileError,
    formValues: formCampanhaValues,
  });

  const handleFileChange = (selectedFile?: File): void => {
    setFile(selectedFile);
    setProgress(0);
  };

  const handleUploadProgress = (event: CampaignUploadProgressEvent): void => {
    if (event.total) {
      setProgress(Math.round((event.loaded * 100) / event.total));
    }
  };

  const generateErrorCsv = (data: string): Blob => {
    const campaignFileName = getCampaignFileNameBase(campaignName);

    return ExportDataDelimited.toCSV(
      parseCampaignErrorCsv(data),
      'csv',
      `${campaignFileName}-planilha_para_correcao`,
    );
  };

  const getErrorsDetails = async (
    detailedList: CampaignUploadResult,
    currentCampaignId: number,
    isDownload: boolean,
  ): Promise<void> => {
    let csvError: Blob | undefined;

    try {
      if (detailedList.listaComErro.length) {
        const response = await campanhaService.getErrorsCsv(
          detailedList.listaComErro,
        );
        if (response) csvError = generateErrorCsv(response);
      }

      setOpen(true);
      await generatePdf(
        detailedList,
        campaignName,
        currentCampaignId,
        csvError,
        isDownload,
      );
    } catch {
      setOpen(false);
      setShowErrorModal(true);
      await campanhaService.deleteCampanha(String(currentCampaignId));
    }
  };

  const uploadCampaignFile = async (
    currentCampaignId: number,
    currentFile: File,
    isDownload = false,
  ): Promise<void> => {
    const shouldTrackProgress = !isDownload;

    if (shouldTrackProgress) {
      setIsUploading(true);
      setProgress(0);
    }

    try {
      const uploadFile = new File(
        [currentFile],
        getCampaignUploadFileName(campaignName, currentFile.name),
        {
          lastModified: currentFile.lastModified,
          type: currentFile.type,
        },
      );
      const response = (await campanhaService.sendCsvData(
        currentCampaignId,
        uploadFile,
        shouldTrackProgress ? handleUploadProgress : () => undefined,
      )) as CampaignUploadResult;

      if (shouldTrackProgress) setProgress(100);
      setModalInformation(getCampaignModalInformation(response));
      await getErrorsDetails(response, currentCampaignId, isDownload);
    } catch {
      setShowErrorModal(true);
      await campanhaService.deleteCampanha(String(currentCampaignId));
    } finally {
      if (shouldTrackProgress) setIsUploading(false);
    }
  };

  const submitCampaign = async (): Promise<void> => {
    if (!file || isSubmitting) return;

    setIsSubmitting(true);
    setProgress(0);

    try {
      const response = await campanhaService.criarCampanha(
        getCreateCampaignPayload(formCampanhaValues),
      );

      if (response.id) {
        setCampaignId(response.id);
        await uploadCampaignFile(response.id, file);
      }
    } catch {
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadCampaignResult = async (): Promise<void> => {
    if (!campaignId || !file) return;

    await uploadCampaignFile(campaignId, file, true);
  };

  return {
    downloadCampaignResult,
    file,
    fileError,
    handleFileChange,
    isFormInvalid,
    isSubmitting,
    isUploading,
    modalInformation,
    open,
    progress,
    setFileError,
    showErrorModal,
    submitCampaign,
  };
};
